import {
  faArrowsRotate,
  faChevronLeft,
  faChevronRight,
  faPen
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactTable, RowData } from '@tanstack/react-table'
import { motion } from 'motion/react'
import {
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { Dropdown } from '~/components/Form/Input/Dropdown'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { ButtonGroup } from '../Global/Blocks/ButtonGroup/ButtonGroup'
import {
  paginationControls,
  paginationInfo,
  paginationPageEditable,
  paginationPageEditIcon,
  paginationPageEditIconSlot,
  paginationPageInput,
  paginationPageLabel,
  paginationPageSize,
  paginationStart,
  paginationWrapper
} from './DataTable.css'
import type { DataTableFeatures } from './dataTableFeatures'

type DataTablePaginationProps<T extends RowData> = {
  table: ReactTable<DataTableFeatures, T>
  totalItems: number
  onRefresh?: () => void
  isRefreshing?: boolean
  columnPicker?: ReactNode
}

type PaginationPagePickerProps = {
  pageIndex: number
  pageCount: number
  onPageChange: (pageIndex: number) => void
}

const PaginationPagePicker = ({
  pageIndex,
  pageCount,
  onPageChange
}: PaginationPagePickerProps): ReactElement => {
  const currentPage = pageIndex + 1
  const [isEditing, setIsEditing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [draft, setDraft] = useState<number | ''>(currentPage)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) {
      setDraft(currentPage)
    }
  }, [currentPage, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const applyPage = useCallback(
    (page: number) => {
      const clamped = Math.min(Math.max(page, 1), pageCount)
      onPageChange(clamped - 1)
      setDraft(clamped)
    },
    [onPageChange, pageCount]
  )

  const debouncedApply = useDebouncedCallback(applyPage, 300)

  const startEditing = () => {
    setDraft(currentPage)
    setIsEditing(true)
    setIsHovered(false)
  }

  const finishEditing = () => {
    debouncedApply.cancel()
    applyPage(draft === '' ? currentPage : draft)
    setIsEditing(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      finishEditing()
    }
    if (e.key === 'Escape') {
      debouncedApply.cancel()
      setDraft(currentPage)
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <span className={paginationPageLabel}>
        Page{' '}
        <input
          ref={inputRef}
          type="number"
          min={1}
          max={pageCount}
          className={paginationPageInput}
          value={draft}
          aria-label="Numéro de page"
          onChange={(e) => {
            const { value, valueAsNumber } = e.target
            if (value === '') {
              setDraft('')
              return
            }
            if (!Number.isNaN(valueAsNumber)) {
              setDraft(valueAsNumber)
              debouncedApply(valueAsNumber)
            }
          }}
          onBlur={finishEditing}
          onKeyDown={handleKeyDown}
        />{' '}
        / {pageCount}
      </span>
    )
  }

  return (
    <span className={paginationPageLabel}>
      Page{' '}
      <button
        type="button"
        className={paginationPageEditable}
        onClick={startEditing}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Cliquer pour aller à une page"
      >
        {currentPage}
        <motion.span
          className={paginationPageEditIconSlot}
          initial={false}
          animate={{
            width: isHovered ? 12 : 0,
            marginLeft: isHovered ? 4 : 0,
            opacity: isHovered ? 1 : 0
          }}
          transition={{ duration: 0.12 }}
        >
          <motion.span
            className={paginationPageEditIcon}
            initial={false}
            animate={{ scale: isHovered ? 1 : 0.85 }}
            transition={{ duration: 0.12 }}
          >
            <FontAwesomeIcon icon={faPen} />
          </motion.span>
        </motion.span>
      </button>{' '}
      / {pageCount}
    </span>
  )
}

const DataTablePagination = <T extends RowData>({
  table,
  totalItems,
  onRefresh,
  isRefreshing = false,
  columnPicker
}: DataTablePaginationProps<T>): ReactElement => {
  const { pageIndex, pageSize } = table.state.pagination
  const pageCount = table.getPageCount()

  return (
    <div className={paginationWrapper}>
      <div className={paginationStart}>
        {columnPicker}
        <span className={paginationInfo}>
          {totalItems === 0
            ? '0 résultat'
            : `${pageIndex * pageSize + 1}–${Math.min((pageIndex + 1) * pageSize, totalItems)} sur ${totalItems} résultats`}
        </span>
      </div>
      <div className={paginationControls}>
        {onRefresh && (
          <Button
            buttonSize="small"
            variation={ButtonVariation.Secondary}
            narrow
            title="Actualiser"
            disabled={isRefreshing}
            onClick={onRefresh}
          >
            <FontAwesomeIcon icon={faArrowsRotate} spin={isRefreshing} />
          </Button>
        )}
        <Dropdown
          key={pageSize}
          options={[10, 20, 30, 50].map((s) => ({
            label: `${s} par page`,
            value: String(s)
          }))}
          defaultValue={String(pageSize)}
          small
          required
          onChange={(value) => table.setPageSize(Number(value))}
          wrapperClassName={paginationPageSize}
        />
        <ButtonGroup>
          <Button
            buttonSize="small"
            variation={ButtonVariation.Secondary}
            narrow
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          {totalItems === 0 ? (
            <span className={paginationInfo}>Page 1</span>
          ) : (
            <span className={paginationInfo}>
              <PaginationPagePicker
                pageIndex={pageIndex}
                pageCount={pageCount}
                onPageChange={(next) => table.setPageIndex(next)}
              />
            </span>
          )}
          <Button
            buttonSize="small"
            variation={ButtonVariation.Secondary}
            narrow
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}

export default DataTablePagination
